import type { NoteName, Scale } from '@tunasong/schemas'
import type { Chord, ChordVariant } from '../chord/chord-types.js'
import { isChordVariant } from '../chord/chord-types.js'
import { getNoteChromaticIndex } from '../note/note-lib.js'
import { scaleNoteNames } from './key.js'

/**
 * The song's home key from its chords (GH #2750; spec docs/ux/2026-10-24-song-key.md §5, Bowie's verdict).
 *
 * Every section's chords are pooled, weighted by bars (or one per chord when bars are unset) and 1.5× in a chorus,
 * then scored against each key on chord roots and qualities, not collapsed notes: an extended tonic (`Cmaj7`) is I
 * and a minor key's major V is diatonic. The tonic's share, the last chord (strongest), the first chord and V→I
 * cadences add to the fit. Mixolydian and Dorian are scored too, so a modal feel shows instead of a wrong key.
 */

export interface SongKeySection {
  sectionType?: string
  bars?: number
  chords: Chord[]
}

export type SongKeyRecommendation =
  | { kind: 'none'; reason: 'too-few-chords' | 'unclear' }
  | { kind: 'likely'; key: Scale }
  /** The relative major and minor within `closeCallMargin`: major first */
  | { kind: 'close-call'; keys: [Scale, Scale] }
  /** `key` is the parallel major (Mixolydian) or minor (Dorian) the feel is heard against */
  | { kind: 'modal'; key: Scale; mode: 'mixolydian' | 'dorian' }

/** The weights, exported so the theory review can see and tune them (Bowie) */
export const songKeyWeights = {
  /** Share of the weighted chords that are diatonic to the key */
  fit: 0.6,
  /** Share of the weighted chords that are the tonic chord */
  tonic: 0.18,
  /** The song ends on the tonic chord: the strongest single cue */
  lastChord: 0.12,
  /** The song starts on the tonic chord */
  firstChord: 0.04,
  /** Of the arrivals at the tonic chord from another chord, the share that come from V (Bowie: real cadences) */
  cadence: 0.06,
  /** A chorus counts this much more than other sections */
  chorus: 1.5,
}

/** Bowie §5.4: no suggestion below six chords or two distinct roots */
export const minChords = 6
export const minRoots = 2
/**
 * Bowie §5.4: "likely" needs most of the music to sit in the key. A last chorus up a step keeps a fit of 0.78–0.85,
 * and chords from no key in particular stay under 0.35, so the gate sits between them (agreed by Bowie).
 */
export const minFit = 0.7
/** Bowie §5.3: the relative major and minor within about 10% are a close call */
export const closeCallMargin = 0.1

type Quality = 'major' | 'minor' | 'diminished' | 'augmented' | 'neutral'
type KeyKind = 'major' | 'minor' | 'mixolydian' | 'dorian'

/**
 * Triad quality from the chord's variant; sevenths and extensions keep their triad's quality. A chord with no third
 * (any sus, a power chord, an 11) is neutral, and an augmented seventh is a dominant, as 7#5 is (Bowie).
 */
const qualityOf = (variant: string): Quality => {
  if (/^(dim|mb5|m7b5|min7b5|ø)/.test(variant)) {
    return 'diminished'
  }
  if (variant.includes('sus') || variant === '5' || variant === '11') {
    return 'neutral'
  }
  if (variant === 'aug7' || variant === '+7') {
    return 'major'
  }
  if (/^(aug|\+)/.test(variant)) {
    return 'augmented'
  }
  if (/^m(?!aj)/.test(variant) || variant.startsWith('min')) {
    return 'minor'
  }
  return 'major'
}

/** Diatonic chords per key, as semitones above the tonic with the qualities that count */
const diatonic: Record<KeyKind, Record<number, Quality[]>> = {
  major: { 0: ['major'], 2: ['minor'], 4: ['minor'], 5: ['major'], 7: ['major'], 9: ['minor'], 11: ['diminished'] },
  /** Natural minor plus the harmonic minor's major V and leading-tone vii° (Bowie §5.3, review item 5) */
  minor: {
    0: ['minor'],
    2: ['diminished'],
    3: ['major'],
    5: ['minor'],
    7: ['minor', 'major'],
    8: ['major'],
    10: ['major'],
    11: ['diminished'],
  },
  mixolydian: {
    0: ['major'],
    2: ['minor'],
    4: ['diminished'],
    5: ['major'],
    7: ['minor'],
    9: ['minor'],
    10: ['major'],
  },
  dorian: { 0: ['minor'], 2: ['minor'], 3: ['major'], 5: ['major'], 7: ['minor'], 9: ['diminished'], 10: ['major'] },
}

const tonicQuality: Record<KeyKind, Quality> = { major: 'major', minor: 'minor', mixolydian: 'major', dorian: 'minor' }

interface Played {
  pitch: number
  root: NoteName
  quality: Quality
  weight: number
}

const fits = (allowed: Quality[] | undefined, quality: Quality) =>
  Boolean(allowed) && (quality === 'neutral' || (allowed?.includes(quality) ?? false))

const isTonic = (chord: Played, tonic: number, kind: KeyKind) =>
  chord.pitch === tonic && fits([tonicQuality[kind]], chord.quality)

const isDominant = (chord: Played, tonic: number) => chord.pitch === (tonic + 7) % 12 && chord.quality === 'major'

const isDiatonic = (chord: Played, tonic: number, kind: KeyKind) =>
  fits(diatonic[kind][(chord.pitch - tonic + 12) % 12], chord.quality)

/**
 * A secondary dominant (Bowie, review item 6): a major or dominant chord whose next chord is a perfect fifth below,
 * diatonic, and not the tonic (V→I is diatonic already). `C E7 Am A7 Dm G7 C` is all C major.
 */
const isSecondaryDominant = (chord: Played, next: Played | undefined, tonic: number, kind: KeyKind) =>
  chord.quality === 'major' &&
  Boolean(next) &&
  next?.pitch === (chord.pitch + 5) % 12 &&
  isDiatonic(next, tonic, kind) &&
  !isTonic(next, tonic, kind)

const score = (played: Played[], tonic: number, kind: KeyKind) => {
  const total = played.reduce((sum, chord) => sum + chord.weight, 0)
  const fitting = played.filter(
    (chord, index) => isDiatonic(chord, tonic, kind) || isSecondaryDominant(chord, played[index + 1], tonic, kind)
  )
  const fit = fitting.reduce((sum, chord) => sum + chord.weight, 0) / total
  const share = (test: (chord: Played) => boolean) =>
    played.filter(test).reduce((sum, chord) => sum + chord.weight, 0) / total
  /** Of the arrivals at the tonic chord from a different chord, the share that come from V */
  const arrivals = played
    .slice(1)
    .map((chord, index) => [played[index], chord] as const)
    .filter(([from, to]) => from && isTonic(to, tonic, kind) && !isTonic(from, tonic, kind))
  const cadences =
    arrivals.length === 0 ? 0 : arrivals.filter(([from]) => from && isDominant(from, tonic)).length / arrivals.length
  const first = played[0]
  const last = played[played.length - 1]
  const value =
    songKeyWeights.fit * fit +
    songKeyWeights.tonic * share(chord => isTonic(chord, tonic, kind)) +
    songKeyWeights.lastChord * (last && isTonic(last, tonic, kind) ? 1 : 0) +
    songKeyWeights.firstChord * (first && isTonic(first, tonic, kind) ? 1 : 0) +
    songKeyWeights.cadence * cadences
  return { tonic, kind, fit, value }
}

const playedChords = (sections: SongKeySection[]): Played[] =>
  sections.flatMap(section => {
    const variants = section.chords.filter((chord): chord is ChordVariant => isChordVariant(chord))
    const perChord = section.bars && section.bars > 0 && variants.length > 0 ? section.bars / variants.length : 1
    const chorus = section.sectionType?.toLowerCase() === 'chorus' ? songKeyWeights.chorus : 1
    return variants.flatMap(chord => {
      const pitch = getNoteChromaticIndex(chord.root)
      return pitch < 0
        ? []
        : [{ pitch, root: chord.root, quality: qualityOf(chord.variant), weight: perChord * chorus }]
    })
  })

/** Tonics whose key would need more than seven sharps or flats, and their usual spelling (Bowie, review item 7) */
const respell: Record<'major' | 'minor', Partial<Record<string, NoteName>>> = {
  major: { 'A#': 'Bb', 'D#': 'Eb', 'G#': 'Ab', 'E#': 'F', 'B#': 'C', Fb: 'E' },
  minor: { Db: 'C#', Gb: 'F#', Fb: 'E', Cb: 'B', 'E#': 'F', 'B#': 'C' },
}

/** The usual tonic spelling per pitch when the song never plays the tonic as a chord root */
const defaultTonics: Record<'major' | 'minor', NoteName[]> = {
  major: ['C', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'] as NoteName[],
  minor: ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'G#', 'A', 'Bb', 'B'] as NoteName[],
}

/** The tonic as the song spells it (Bb, not A#), unless that key needs more than seven sharps or flats */
const spell = (played: Played[], pitch: number, type: 'major' | 'minor'): NoteName => {
  const written = played.find(chord => chord.pitch === pitch)?.root
  if (!written) {
    return defaultTonics[type][pitch] ?? (scaleNoteNames[pitch] as NoteName)
  }
  return respell[type][written] ?? written
}

export const recommendSongKey = (sections: SongKeySection[]): SongKeyRecommendation => {
  const played = playedChords(sections)
  if (played.length < minChords || new Set(played.map(chord => chord.pitch)).size < minRoots) {
    return { kind: 'none', reason: 'too-few-chords' }
  }

  const kinds: KeyKind[] = ['major', 'minor', 'mixolydian', 'dorian']
  const first = played[0]
  const last = played[played.length - 1]
  /** Each root's total weight: a modal tonic must be strictly the heaviest (ties don't count) */
  const rootWeight = new Map<number, number>()
  played.forEach(chord => rootWeight.set(chord.pitch, (rootWeight.get(chord.pitch) ?? 0) + chord.weight))
  const isHeaviest = (pitch: number) =>
    [...rootWeight].every(([other, weight]) => other === pitch || weight < (rootWeight.get(pitch) ?? 0))
  /** The chord that sets the mode apart from major or minor: without one they fit identically (Bowie) */
  const hasCharacteristicChord = (tonic: number, kind: KeyKind) =>
    played.some(chord => {
      const degree = (chord.pitch - tonic + 12) % 12
      return kind === 'mixolydian'
        ? (degree === 10 && chord.quality === 'major') || (degree === 7 && chord.quality === 'minor')
        : (degree === 5 && chord.quality === 'major') || (degree === 2 && chord.quality === 'minor')
    })
  /**
   * A modal reading (Bowie, review item 1): its tonic chord is the first or the last chord, its root is strictly the
   * heaviest, and the song has the mode's characteristic chord. vi–IV–I–V looped never reads as Mixolydian on V,
   * since G only ties; A G D A charted to end on E still reads A Mixolydian.
   */
  const isModal = (tonic: number, kind: KeyKind) =>
    Boolean((first && isTonic(first, tonic, kind)) || (last && isTonic(last, tonic, kind))) &&
    isHeaviest(tonic) &&
    hasCharacteristicChord(tonic, kind)
  const ranked = Array.from({ length: 12 }, (_, tonic) => kinds.map(kind => score(played, tonic, kind)))
    .flat()
    .filter(
      candidate => candidate.kind === 'major' || candidate.kind === 'minor' || isModal(candidate.tonic, candidate.kind)
    )
    .sort((a, b) => b.value - a.value)
  const best = ranked[0]
  if (!best || best.fit < minFit) {
    return { kind: 'none', reason: 'unclear' }
  }

  const key = (tonic: number, type: 'major' | 'minor'): Scale => ({ root: spell(played, tonic, type), type })
  if (best.kind === 'mixolydian' || best.kind === 'dorian') {
    return { kind: 'modal', key: key(best.tonic, best.kind === 'mixolydian' ? 'major' : 'minor'), mode: best.kind }
  }

  const relativeTonic = best.kind === 'major' ? (best.tonic + 9) % 12 : (best.tonic + 3) % 12
  const relativeKind: KeyKind = best.kind === 'major' ? 'minor' : 'major'
  const relative = ranked.find(candidate => candidate.tonic === relativeTonic && candidate.kind === relativeKind)
  if (relative && best.value - relative.value <= closeCallMargin * best.value) {
    const [major, minor] = best.kind === 'major' ? [best, relative] : [relative, best]
    return { kind: 'close-call', keys: [key(major.tonic, 'major'), key(minor.tonic, 'minor')] }
  }
  return { kind: 'likely', key: key(best.tonic, best.kind) }
}
